import { TopBar, TopBarInfo } from '@statsbomb/kitbag-components';
import { ReactNode, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
import { useApplicationReferenceData } from '@/hooks/dataFetching/useApplicationReferenceData';
import { useFilterStateHistory } from '@/hooks/useFilterStateHistory';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import { useOnBackButtonClick } from '@/hooks/useOnBackButtonClick';
import { GenericToast } from '../alerts/GenericToast/GenericToast';
import { NoTextLoader } from '../contentState/NoTextLoader';
import { DefaultTeamPromptModal } from '../modal/DefaultTeamPromptModal/DefaultTeamPromptModal';
import { FirstTimeModalTranslations } from '../modal/FirstTimeModals/FirstTimeModalTranslations';
import { CrossPlatformSideNav } from '../navigation/crossPlatform/CrossPlatformSideNav';

interface AppShellProps {
  topBar: ReactNode;
  subNav?: ReactNode;
  children: ReactNode;
}

export const AppShell = () => {
  // Nothing here may suspend: the shell (side nav + top bar) must paint as soon as the
  // licence check clears, with page content streaming in behind AppShellContents'
  // Suspense boundary. The side nav reads only unwrapped navigation atoms, so its link
  // targets fill in as PrefetchNavigationData's queries resolve rather than gating the
  // whole app. useApplicationReferenceData's queries are all wrapped in `loadable`.
  useApplicationReferenceData();

  return (
    <>
      <div className="flex h-full isolate" data-testid="appshell">
        <div className="z-10">
          <CrossPlatformSideNav />
        </div>
        <Outlet />
      </div>
      <GenericToast />
      <DefaultTeamPromptModal />
      <FirstTimeModalTranslations />
    </>
  );
};

export const AppShellContentsBase = ({ topBar, subNav, children }: AppShellProps) => {
  useFilterStateHistory();
  useOnBackButtonClick();

  const isTouch = useIsTouchDevice();

  return (
    <div className="flex flex-col w-full overflow-x-hidden grow appbar-wrapper">
      <TopBar>
        <TopBarInfo isTouch={isTouch}>{topBar}</TopBarInfo>
        {subNav}
      </TopBar>
      {children}
    </div>
  );
};

export const AppShellContents = ({ topBar, subNav, children }: AppShellProps) => (
  <AppShellContentsBase topBar={topBar} subNav={subNav}>
    <Suspense fallback={<NoTextLoader />}>{children}</Suspense>
  </AppShellContentsBase>
);
